import { useEffect, useMemo, useState, type ReactNode } from "react";
import axios from "axios";
import { PrismBanner } from "@prism-ui/react";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import {
  CHART_MEASURES,
  MEASURES,
  TENURE,
  TRACKS,
  chapterColor,
  chapterSeries,
  dayLabel,
  funnelSeries,
  isDecimal,
  paceSeries,
  paceYears,
  tenureSeries,
  trackSeries,
  yearColor,
  yearLabel,
  type HistoryData,
  type MeasureKey,
  type Point,
  type Series,
} from "./history";
import "./ApplicationHistory.css";

const ALL = "all";
const parseId = (value: string) => (value === ALL ? null : Number(value));

interface ChartCardProps {
  title: string;
  description: ReactNode;
  controls: ReactNode;
  series: Series[];
  data: Point[];
  xKey: "year" | "day";
  currentYear: number;
  // Pace charts emphasise the line for this series key (the current year).
  emphasize?: string;
  decimals?: boolean;
}

// One chart with its own on/off chips, hover tooltip and a "Show the numbers" table.
const ChartCard = ({
  title,
  description,
  controls,
  series,
  data,
  xKey,
  currentYear,
  emphasize,
  decimals = false,
}: ChartCardProps) => {
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const toggle = (key: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const allHidden = series.every((s) => hidden.has(s.key));
  const formatX = (value: number) => (xKey === "day" ? dayLabel(value) : yearLabel(value, currentYear));

  // Hollow, larger dot marks the in-progress year so it isn't read as a finished one.
  const renderDot = (color: string) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function HistoryDot(props: any) {
      const { cx, cy, payload, value, index } = props;
      if (cx == null || cy == null || value == null) return <g key={index} />;
      const current = payload?.year === currentYear;
      return (
        <circle
          key={index}
          cx={cx}
          cy={cy}
          r={current ? 5 : 3.5}
          stroke={color}
          strokeWidth={2}
          fill={color}
          className={current ? "ah-dot-current" : undefined}
        />
      );
    };

  return (
    <section className="ah-card" aria-label={title}>
      <header className="ah-card-head">
        <div>
          <h3>{title}</h3>
          <p className="ah-card-desc">{description}</p>
        </div>
        <div className="ah-controls">{controls}</div>
      </header>

      <div className="ah-toggles" role="group" aria-label={`Lines on ${title}`}>
        {series.map((s) => {
          const on = !hidden.has(s.key);
          return (
            <button
              key={s.key}
              type="button"
              className={`ah-chip${on ? " on" : ""}`}
              aria-pressed={on}
              style={{ "--ah-chip": s.color } as React.CSSProperties}
              onClick={() => toggle(s.key)}
            >
              <span className="ah-chip-dot" aria-hidden="true" />
              {s.label}
            </button>
          );
        })}
        <span className="ah-toggle-all">
          <button type="button" className="ah-link" onClick={() => setHidden(new Set())} disabled={hidden.size === 0}>
            Show all
          </button>
          <button
            type="button"
            className="ah-link"
            onClick={() => setHidden(new Set(series.map((s) => s.key)))}
            disabled={allHidden}
          >
            Hide all
          </button>
        </span>
      </div>

      <div className="ah-chart">
        {data.length === 0 ? (
          <p className="ah-empty">No data for this selection.</p>
        ) : (
          <>
            {allHidden && <p className="ah-empty ah-overlay">Every line is hidden. Turn one back on above.</p>}
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={data} margin={{ top: 12, right: 24, bottom: 4, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="ah-grid" />
                <XAxis
                  dataKey={xKey}
                  type={xKey === "day" ? "number" : "category"}
                  domain={xKey === "day" ? ["dataMin", "dataMax"] : undefined}
                  tickFormatter={formatX}
                  className="ah-axis"
                  minTickGap={16}
                />
                <YAxis allowDecimals={decimals} className="ah-axis" width={44} />
                <Tooltip
                  labelFormatter={(label) => formatX(Number(label))}
                  wrapperClassName="ah-tooltip"
                  contentStyle={{
                    background: "var(--color-surface)",
                    border: "1px solid var(--color-border)",
                    borderRadius: 10,
                    color: "var(--color-text)",
                  }}
                />
                {series.map((s) => (
                  <Line
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    name={s.label}
                    stroke={s.color}
                    strokeWidth={s.key === emphasize ? 3.5 : 2.25}
                    hide={hidden.has(s.key)}
                    dot={xKey === "day" ? false : renderDot(s.color)}
                    activeDot={{ r: 6 }}
                    isAnimationActive={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </>
        )}
      </div>

      <details className="ah-numbers">
        <summary>Show the numbers</summary>
        <div className="ah-table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">{xKey === "day" ? "Date" : "Year"}</th>
                {series.map((s) => (
                  <th key={s.key} scope="col">
                    {s.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(xKey === "day" ? data.filter((_point, i) => i % 7 === 0 || i === data.length - 1) : data).map((p) => (
                <tr key={String(p[xKey])}>
                  <th scope="row">{formatX(Number(p[xKey]))}</th>
                  {series.map((s) => (
                    <td key={s.key}>{p[s.key] ?? "–"}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </section>
  );
};

const Select = ({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}) => (
  <label className="ah-select">
    <span>{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  </label>
);

const measureOptions = CHART_MEASURES.map((m) => ({ value: m.key, label: m.label }));
const trackOptions = [
  { value: ALL, label: "All tracks" },
  ...TRACKS.map((t) => ({ value: String(t.id), label: t.label })),
];

// Year-over-year application numbers for every chapter. Aggregates only, so it is the same for
// everyone who can see it.
const ApplicationHistory = ({ defaultChapterId = null }: { defaultChapterId?: number | null }) => {
  const [data, setData] = useState<HistoryData | null>(null);
  const [failed, setFailed] = useState(false);

  const [compareMeasure, setCompareMeasure] = useState<MeasureKey>("submitted");
  const [compareTrack, setCompareTrack] = useState(ALL);
  const [funnelChapter, setFunnelChapter] = useState(defaultChapterId ? String(defaultChapterId) : ALL);
  const [funnelTrack, setFunnelTrack] = useState(ALL);
  const [trackChapter, setTrackChapter] = useState(defaultChapterId ? String(defaultChapterId) : ALL);
  const [trackMeasure, setTrackMeasure] = useState<MeasureKey>("submitted");
  const [paceChapter, setPaceChapter] = useState(defaultChapterId ? String(defaultChapterId) : ALL);
  const [tenureChapter, setTenureChapter] = useState(defaultChapterId ? String(defaultChapterId) : ALL);
  const [tenureTrack, setTenureTrack] = useState(ALL);

  useEffect(() => {
    let live = true;
    axios
      .get<HistoryData>("/api/applications_hub/history")
      .then(({ data: body }) => live && setData(body))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, []);

  const chapterOptions = useMemo(
    () => [
      { value: ALL, label: "All chapters" },
      ...(data?.chapters ?? []).map((c) => ({
        value: String(c.id),
        label: c.name,
      })),
    ],
    [data]
  );

  if (failed) return <p className="ah-empty">We couldn&apos;t load application history.</p>;
  if (!data)
    return (
      <p className="ah-empty" role="status">
        Loading history…
      </p>
    );

  const current = data.current_year;
  const chapterName = (value: string) => chapterOptions.find((o) => o.value === value)?.label ?? "";
  // "Applications submitted" for the funnel measures; the review measures read on their own.
  const measureName = (key: MeasureKey) => {
    const label = CHART_MEASURES.find((m) => m.key === key)?.label ?? key;
    return MEASURES.some((m) => m.key === key) ? `Applications ${label.toLowerCase()}` : label;
  };
  const chapterLines: Series[] = data.chapters.map((c, i) => ({
    key: String(c.id),
    label: c.name,
    color: chapterColor(i),
  }));
  const years = paceYears(data);

  return (
    <div className="ah">
      <PrismBanner tone="info">
        How applications have grown year over year, for every chapter. These are counts only, with no names or personal
        details, so everyone sees the same numbers. Click a colored chip to turn a line on or off, hover the chart for
        exact numbers, and open &ldquo;Show the numbers&rdquo; for a table. {current} is still in progress and is drawn
        with a hollow dot.
      </PrismBanner>

      <ChartCard
        title="Chapters compared"
        description={`${measureName(compareMeasure)} each year, one line per chapter.`}
        controls={
          <>
            <Select
              label="Measure"
              value={compareMeasure}
              onChange={(v) => setCompareMeasure(v as MeasureKey)}
              options={measureOptions}
            />
            <Select label="Track" value={compareTrack} onChange={setCompareTrack} options={trackOptions} />
          </>
        }
        series={chapterLines}
        data={chapterSeries(data, compareMeasure, parseId(compareTrack))}
        xKey="year"
        currentYear={current}
        decimals={isDecimal(compareMeasure)}
      />

      <ChartCard
        title="Enrollment funnel"
        description={`From started to decided, each year, for ${funnelChapter === ALL ? "all chapters" : chapterName(funnelChapter)}. "Submitted" counts every application that was ever submitted.`}
        controls={
          <>
            <Select label="Chapter" value={funnelChapter} onChange={setFunnelChapter} options={chapterOptions} />
            <Select label="Track" value={funnelTrack} onChange={setFunnelTrack} options={trackOptions} />
          </>
        }
        series={MEASURES.map((m) => ({
          key: m.key,
          label: m.label,
          color: m.color,
        }))}
        data={funnelSeries(data, parseId(funnelChapter), parseId(funnelTrack))}
        xKey="year"
        currentYear={current}
      />

      <ChartCard
        title="Tracks"
        description={`${measureName(trackMeasure)} each year by track. Committee Members started in 2024.`}
        controls={
          <>
            <Select label="Chapter" value={trackChapter} onChange={setTrackChapter} options={chapterOptions} />
            <Select
              label="Measure"
              value={trackMeasure}
              onChange={(v) => setTrackMeasure(v as MeasureKey)}
              options={measureOptions}
            />
          </>
        }
        series={TRACKS.map((t) => ({
          key: String(t.id),
          label: t.label,
          color: t.color,
        }))}
        data={trackSeries(data, parseId(trackChapter), trackMeasure)}
        xKey="year"
        currentYear={current}
        decimals={isDecimal(trackMeasure)}
      />

      <ChartCard
        title="Tenure at application"
        description={`How long applicants had been at the company when they applied, for ${tenureChapter === ALL ? "all chapters" : chapterName(tenureChapter)}.`}
        controls={
          <>
            <Select label="Chapter" value={tenureChapter} onChange={setTenureChapter} options={chapterOptions} />
            <Select label="Track" value={tenureTrack} onChange={setTenureTrack} options={trackOptions} />
          </>
        }
        series={TENURE.map((t) => ({ key: t.key, label: t.label, color: t.color }))}
        data={tenureSeries(data, parseId(tenureChapter), parseId(tenureTrack))}
        xKey="year"
        currentYear={current}
      />

      <ChartCard
        title="Enrollment pace"
        description={`Running total of submissions through each cohort year, so you can see how this year compares at the same point. ${current} is the bold line and stops at today.`}
        controls={<Select label="Chapter" value={paceChapter} onChange={setPaceChapter} options={chapterOptions} />}
        series={years.map((y, i) => ({
          key: String(y),
          label: String(y),
          color: yearColor(y, current, i),
        }))}
        data={paceSeries(data, parseId(paceChapter))}
        xKey="day"
        currentYear={current}
        emphasize={String(current)}
      />
    </div>
  );
};

export default ApplicationHistory;
